import { initComponent } from "../../utilities/common";
import { remoteComponentBind } from "../remote-trigger/browser";
import styles from "./styles.css";

const LABEL_DISPLAY_DURATION = 8000; // Duration in ms to show label on page load
const AUTO_COLLAPSE_DURATION = 8000; // Duration in ms before child FABs auto-collapse
const HOVER_OUT_GRACE_PERIOD = 300; // Duration in ms to wait before hiding label on hover out

export default class EnhancedPersistentCTA {
  constructor(element) {
    this.element = element;
    this.mainFab = element.querySelector("[data-main-fab]");
    this.label = element.querySelector("[data-label]");
    this.childFabsContainer = element.querySelector("[data-child-fabs]");
    this.childFabs = Array.from(element.querySelectorAll("[data-cta-button]"));
    this.modalIds = this.childFabs
      .filter(
        (childFab) =>
          childFab.dataset.ctaType === "form" &&
          childFab.dataset.remoteTriggerId
      )
      .map((childFab) => childFab.dataset.remoteTriggerId);
    this.isExpanded = false;
    this.labelTimeout = null;
    this.hideLabelTimeout = null;
    this.autoCollapseTimeout = null;
    this.hasLabel = element.dataset.noLabel === undefined;
    this.forceShowLabel = false;
    this.isOpeningChildFabs = false;
    this.justOpenedFromModalClose = false;
    this.expandedWidthValid = false;

    // Bind methods that will be used as event handlers
    this.handleChildButtonClick = this.handleChildButtonClick.bind(this);

    // Add data attribute to body to indicate enhanced persistent CTA is present
    document.body.setAttribute("data-has-enhanced-persistent-cta", "");

    // Initialize child buttons as not keyboard-focusable (will be enabled when expanded)
    this.childFabs.forEach((childFab) => {
      childFab.setAttribute("tabindex", "-1");
    });

    this.init();
  }

  init() {
    // Hide chat FAB buttons immediately with CSS to prevent flash
    this.hideChatFabButtonsImmediately();

    // Unhide the chat component so it can receive remote triggers
    // (Only if chat is not hidden by default in CMS)
    this.unhideChatComponent();

    // Bind remote triggers for all child buttons
    this.bindChildButtonRemoteTriggers();

    // Show label temporarily on page load if label exists
    if (this.hasLabel && this.label) {
      this.showLabelTemporarily();

      // Deliberately not measured here: this component's own stylesheet and
      // the background component's are both lazy-loaded, so at construction
      // the background div is still in flow and inflates the width by one
      // flex gap. Measure once everything has landed, and again whenever the
      // rendered text width could have changed.
      this.element.cssLoadedCallback = () => this.invalidateExpandedWidth();
      if (this.element.hasAttribute("css-loaded")) {
        this.invalidateExpandedWidth();
      }
      document.fonts?.ready?.then(() => this.invalidateExpandedWidth());
    }

    // Main FAB click handler
    this.mainFab.addEventListener("click", () => {
      this.toggleChildFabs();
    });

    // Main FAB hover handlers (only if label exists)
    if (this.hasLabel && this.label) {
      this.mainFab.addEventListener("mouseenter", () => {
        this.showLabel();
        this.resetAutoCollapseTimeout();
      });

      this.mainFab.addEventListener("mouseleave", () => {
        if (!this.isExpanded && !this.forceShowLabel) {
          // Give the user a brief grace period to move onto the button and
          // click it before the label (and its extra click target) disappears.
          this.scheduleHideLabel();
        }
        this.startAutoCollapseTimeout();
      });

      // Keyboard parity: without these a keyboard-only user never sees the
      // label, since the pill would only ever open on activation.
      this.mainFab.addEventListener("focus", () => {
        this.showLabel();
        this.resetAutoCollapseTimeout();
      });

      this.mainFab.addEventListener("blur", () => {
        if (!this.isExpanded && !this.forceShowLabel) {
          this.scheduleHideLabel();
        }
        this.startAutoCollapseTimeout();
      });
    }

    // Child FAB interaction handlers for timeout management
    this.childFabs.forEach((childFab) => {
      // Reset timeout on interaction
      childFab.addEventListener("mouseenter", () => {
        this.resetAutoCollapseTimeout();
      });

      childFab.addEventListener("mouseleave", () => {
        this.startAutoCollapseTimeout();
      });

      childFab.addEventListener("focus", () => {
        this.resetAutoCollapseTimeout();
      });

      // Note: Click handler is in bindChildButtonRemoteTriggers()
    });

    // Listen for chat open/close events
    this.setupChatListeners();

    // Listen for modal open/close events
    this.setupModalListeners();

    // Close child FABs when clicking outside
    document.addEventListener("click", (e) => {
      if (
        !this.element.contains(e.target) &&
        this.isExpanded &&
        !this.justOpenedFromModalClose
      ) {
        this.closeChildFabs();
      }
    });

    // Close child FABs when clicking on empty space within the container
    this.element.addEventListener("click", (e) => {
      // Only close if clicking directly on the container or childFabsContainer, not on buttons
      if (
        (e.target === this.element || e.target === this.childFabsContainer) &&
        this.isExpanded
      ) {
        this.closeChildFabs();
      }
    });

    // Close child FABs when focus moves outside (keyboard navigation)
    document.addEventListener("focusin", (e) => {
      if (!this.element.contains(e.target) && this.isExpanded) {
        this.closeChildFabs();
      }
    });

    // Keyboard accessibility
    this.mainFab.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.toggleChildFabs();
      }
      if (e.key === "Escape" && this.isExpanded) {
        this.closeChildFabs();
      }
    });
  }

  hideChatFabButtonsImmediately() {
    // Hide chat FAB buttons immediately to prevent flash
    this.childFabs.forEach((childFab) => {
      const ctaType = childFab.dataset.ctaType;
      const remoteTriggerId = childFab.dataset.remoteTriggerId;

      if (ctaType === "chat" && remoteTriggerId) {
        const chatComponent = document.getElementById(remoteTriggerId);
        if (chatComponent) {
          const chatFabButton = chatComponent.querySelector("[chatbutton]");
          if (chatFabButton) {
            chatFabButton.style.display = "none";
          }
        }
      }
    });
  }

  unhideChatComponent() {
    // Unhide chat components so they can receive remote triggers
    this.childFabs.forEach((childFab) => {
      const ctaType = childFab.dataset.ctaType;
      const remoteTriggerId = childFab.dataset.remoteTriggerId;

      if (ctaType === "chat" && remoteTriggerId) {
        const chatComponent = document.getElementById(remoteTriggerId);
        if (chatComponent) {
          // Unhide the chat component itself
          chatComponent.setAttribute("aria-hidden", "false");

          // Make sure the chat wrapper starts hidden
          const chatWrapper = chatComponent.querySelector(
            "[chatIframeWrapper]"
          );
          if (chatWrapper) {
            chatWrapper.setAttribute("aria-hidden", "true");
          }
        }
      }
    });
  }

  handleChildButtonClick() {
    // Show main FAB with label, hide child buttons immediately
    this.showMainFabOnly();
  }

  bindChildButtonRemoteTriggers() {
    // Bind remote trigger functionality to each child button
    this.childFabs.forEach((childFab) => {
      // Add click listener using bound method
      childFab.addEventListener("click", this.handleChildButtonClick);

      // Bind remote trigger to handle the actual modal/chat opening
      remoteComponentBind(childFab);
    });
  }

  showLabelTemporarily() {
    // Clear any existing timeout
    if (this.labelTimeout) {
      clearTimeout(this.labelTimeout);
    }

    // Show label
    this.mainFab.classList.add(styles.expanded);

    // Hide label after configured duration
    this.labelTimeout = setTimeout(() => {
      if (!this.isExpanded) {
        this.hideLabel();
      }
    }, LABEL_DISPLAY_DURATION);
  }

  invalidateExpandedWidth() {
    this.expandedWidthValid = false;
    this.measureExpandedWidth();
  }

  measureExpandedWidth() {
    if (!this.hasLabel || !this.label || this.expandedWidthValid) {
      return;
    }

    // The background div is styled by a separately lazy-loaded stylesheet.
    // Until that lands it is position: static, so it counts as a flex item and
    // adds one extra gap -- measuring then bakes in a pill 12px too wide.
    // Refuse to latch a value until it has gone out of flow; showLabel() and
    // the css-loaded hooks will retry.
    const background = this.mainFab.querySelector('[data-type="background"]');
    if (background && getComputedStyle(background).position === "static") {
      return;
    }

    // Lay the pill out at its natural size behind a suppressed transition so
    // the probe itself never animates or paints, then hand the result to CSS.
    const wasExpanded = this.mainFab.classList.contains(styles.expanded);
    const inlineStyle = this.mainFab.style.cssText;

    this.mainFab.classList.add(styles.expanded);
    this.mainFab.style.transition = "none";
    this.mainFab.style.width = "max-content";

    // Exact, not rounded up: ceiling this added a sub-pixel of dead space
    // that `width: auto` would not have produced.
    const width = this.mainFab.getBoundingClientRect().width;

    this.mainFab.style.cssText = inlineStyle;
    if (!wasExpanded) {
      this.mainFab.classList.remove(styles.expanded);
    }

    this.mainFab.style.setProperty("--fab-expanded-width", `${width}px`);
    this.expandedWidthValid = true;
  }

  showLabel() {
    this.measureExpandedWidth();
    this.clearHideLabelTimeout();
    // The page-load timer would otherwise still fire and collapse the label
    // out from under a user who is actively hovering.
    if (this.labelTimeout) {
      clearTimeout(this.labelTimeout);
      this.labelTimeout = null;
    }
    if (this.hasLabel) {
      this.mainFab.classList.add(styles.expanded);
    }
  }

  hideLabel() {
    if (!this.isExpanded && !this.forceShowLabel && this.hasLabel) {
      this.mainFab.classList.remove(styles.expanded);
    }
  }

  scheduleHideLabel() {
    this.clearHideLabelTimeout();
    this.hideLabelTimeout = setTimeout(() => {
      this.hideLabelTimeout = null;
      this.hideLabel();
    }, HOVER_OUT_GRACE_PERIOD);
  }

  clearHideLabelTimeout() {
    if (this.hideLabelTimeout !== null) {
      clearTimeout(this.hideLabelTimeout);
      this.hideLabelTimeout = null;
    }
  }

  moveFocusToMainFabIfNeeded() {
    const activeElement = document.activeElement;

    if (activeElement && this.childFabsContainer?.contains(activeElement)) {
      this.mainFab?.focus?.();
    }
  }

  toggleChildFabs() {
    // Check if chat or modal is open
    const isChatOpen = this.isChatWindowOpen();
    const isModalOpen = this.isModalOpen();

    if (isChatOpen || isModalOpen) {
      // If chat or modal is open, close them and open child FABs
      this.openChildFabs();
    } else if (this.isExpanded) {
      // If already expanded, close
      this.closeChildFabs();
    } else {
      // Otherwise, open
      this.openChildFabs();
    }
  }

  isChatWindowOpen() {
    // Check if component is still in DOM
    if (!this.element.isConnected) return false;
    const chatWrappers = document.querySelectorAll(
      '[data-type="chat"] [chatIframeWrapper]'
    );
    return Array.from(chatWrappers).some(
      (wrapper) => wrapper.getAttribute("aria-hidden") === "false"
    );
  }

  isModalOpen() {
    // Check if component is still in DOM
    if (!this.element.isConnected) return false;
    const modals = this.getTrackedModals();
    return modals.some(
      (modal) => modal.getAttribute("aria-hidden") === "false"
    );
  }

  getTrackedModals() {
    return this.modalIds
      .map((modalId) => document.getElementById(modalId))
      .filter(Boolean);
  }

  openChildFabs() {
    // Set flag to prevent listeners from interfering
    this.isOpeningChildFabs = true;

    // Ensure element is visible
    this.element.style.display = "flex";

    // Close any open chat windows first
    this.closeOpenChatWindows();

    // Close any open modals
    this.closeOpenModals();

    this.isExpanded = true;
    this.forceShowLabel = false; // Allow normal label behavior
    this.mainFab.setAttribute("aria-expanded", "true");
    this.childFabsContainer.setAttribute("aria-hidden", "false");
    this.element.setAttribute("aria-expanded", "true");

    // Make child buttons keyboard focusable
    this.childFabs.forEach((childFab) => {
      childFab.removeAttribute("tabindex");
    });

    // Show label when expanded
    if (this.hasLabel) {
      this.showLabel();
    }

    // Start auto-collapse timeout
    this.startAutoCollapseTimeout();

    // Reset flag after a short delay to allow DOM updates
    setTimeout(() => {
      this.isOpeningChildFabs = false;
    }, 100);
  }

  closeChildFabs() {
    this.moveFocusToMainFabIfNeeded();
    this.isExpanded = false;
    this.forceShowLabel = false; // Reset forced label state
    this.mainFab.setAttribute("aria-expanded", "false");
    this.childFabsContainer.setAttribute("aria-hidden", "true");
    this.element.setAttribute("aria-expanded", "false");

    // Remove child buttons from tab order when collapsed
    this.childFabs.forEach((childFab) => {
      childFab.setAttribute("tabindex", "-1");
    });

    // Clear auto-collapse timeout
    this.clearAutoCollapseTimeout();

    // Hide label when contracted (icon only)
    if (this.hasLabel) {
      this.hideLabel();
    }
  }

  closeOpenChatWindows() {
    // Check if component is still in DOM
    if (!this.element.isConnected) return;
    // Find all chat wrappers and close them
    const chatWrappers = document.querySelectorAll(
      '[data-type="chat"] [chatIframeWrapper]'
    );
    chatWrappers.forEach((wrapper) => {
      if (wrapper.getAttribute("aria-hidden") === "false") {
        wrapper.setAttribute("aria-hidden", "true");
      }
    });
  }

  closeOpenModals() {
    // Check if component is still in DOM
    if (!this.element.isConnected) return;
    // Only close modals launched by this enhanced CTA instance
    const modals = this.getTrackedModals();
    modals.forEach((modal) => {
      if (modal.getAttribute("aria-hidden") === "false") {
        modal.setAttribute("aria-hidden", "true");
      }
    });
  }

  startAutoCollapseTimeout() {
    // Only start timeout if expanded
    if (!this.isExpanded) return;

    this.clearAutoCollapseTimeout();
    this.autoCollapseTimeout = setTimeout(() => {
      if (this.isExpanded) {
        this.closeChildFabs();
      }
    }, AUTO_COLLAPSE_DURATION);
  }

  resetAutoCollapseTimeout() {
    if (this.isExpanded) {
      this.startAutoCollapseTimeout();
    }
  }

  clearAutoCollapseTimeout() {
    if (this.autoCollapseTimeout) {
      clearTimeout(this.autoCollapseTimeout);
      this.autoCollapseTimeout = null;
    }
  }

  handleChatAriaChange(mutation) {
    if (
      mutation.type !== "attributes" ||
      mutation.attributeName !== "aria-hidden"
    ) {
      return;
    }

    const chatWrapper = mutation.target;
    const isHidden = chatWrapper.getAttribute("aria-hidden");

    if (isHidden === "false") {
      // Chat opened - ensure main FAB with label is showing
      // Don't interfere if we're intentionally opening child FABs
      if (!this.isOpeningChildFabs) {
        this.showMainFabOnly();
      }
    } else {
      // Chat closed - reopen child FABs to show full component
      this.openChildFabs();
    }
  }

  handleChatMessage(event) {
    if (event.origin !== window.location.origin) return;

    // Check if it's a chat open event
    if (event.data.type === "chat-opened") {
      this.showMainFabOnly();
    }
    // Check if it's a chat close event
    if (event.data.type === "chat-closed") {
      this.openChildFabs();
    }
  }

  static shouldIgnoreModalVisibilitySync(modal) {
    // Address verification uses a nested modal inside form flows. Closing it
    // should not cause the enhanced CTA menu to reopen.
    return modal?.id?.endsWith("verificationModal");
  }

  setupChatListeners() {
    // Listen for chat opening
    window.addEventListener("message", (event) =>
      this.handleChatMessage(event)
    );

    // Also listen for chat component aria changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => this.handleChatAriaChange(mutation));
    });

    // Observe all chat components
    const chatWrappers = document.querySelectorAll(
      '[data-type="chat"] [chatIframeWrapper]'
    );
    chatWrappers.forEach((wrapper) => {
      observer.observe(wrapper, { attributes: true });
    });
  }

  handleModalAriaChange(mutation, modal) {
    if (
      mutation.type !== "attributes" ||
      mutation.attributeName !== "aria-hidden"
    ) {
      return;
    }

    if (EnhancedPersistentCTA.shouldIgnoreModalVisibilitySync(modal)) {
      return;
    }

    const isHidden = modal.getAttribute("aria-hidden");

    if (isHidden === "false") {
      // Modal opened - ensure main FAB with label is showing
      // Don't interfere if we're intentionally opening child FABs
      if (!this.isOpeningChildFabs) {
        this.showMainFabOnly();
      }
    } else if (isHidden === "true") {
      // Modal closed - reopen child FABs to show full component
      // Set flag immediately to prevent the same click from closing FABs
      this.justOpenedFromModalClose = true;

      // Use a small delay to ensure the modal is fully closed
      setTimeout(() => {
        this.openChildFabs();
      }, 50);

      // Clear flag after enough time for the click event to complete
      setTimeout(() => {
        this.justOpenedFromModalClose = false;
      }, 200);
    }
  }

  setupModalListeners() {
    // Listen only for modals launched by this enhanced CTA instance.
    const modals = this.getTrackedModals();

    modals.forEach((modal) => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) =>
          this.handleModalAriaChange(mutation, modal)
        );
      });

      observer.observe(modal, { attributes: true });
    });
  }

  hide() {
    this.element.style.display = "none";
    this.closeChildFabs();
  }

  showMainFabOnly() {
    this.element.style.display = "flex";
    this.moveFocusToMainFabIfNeeded();
    this.isExpanded = false;
    // aria-expanded tracks the child-FAB disclosure, and the children are
    // being hidden here -- without this the button keeps announcing itself as
    // expanded for as long as the chat/modal stays open.
    this.mainFab.setAttribute("aria-expanded", "false");
    this.childFabsContainer.setAttribute("aria-hidden", "true");
    this.element.setAttribute("aria-expanded", "false");
    this.clearAutoCollapseTimeout();

    // Show label with main FAB (if label exists)
    if (this.hasLabel && this.label) {
      this.forceShowLabel = true;
      this.showLabel();
    }
  }

  show() {
    this.element.style.display = "flex";
  }
}

export const init = () => {
  initComponent("enhanced-persistent-cta", (element) => {
    return new EnhancedPersistentCTA(element);
  });
};

init();
